import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { 
  XMarkIcon, 
  FolderIcon, 
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { userService } from '../services/UserService';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';

function FolderManagerModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const [folders, setFolders] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editName, setEditName] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    const updateFolders = (userData) => {
      if (userData && userData.folders) {
        setFolders(userData.folders);
      } else {
        setFolders([]);
      }
    };

    updateFolders(userService.currentUser);
    const unsubscribe = userService.subscribe(updateFolders);
    return unsubscribe;
  }, []);

  if (!isOpen) return null;

  const handleSaveUserFolders = async (updatedFolders) => {
    if (!userService.currentUser?.uid) return;
    
    try {
      const userRef = doc(db, 'users', userService.currentUser.uid);
      // We need to convert FolderModel instances back to plain objects if they aren't already
      const foldersPlain = updatedFolders.map(f => ({
        id: f.id,
        name: f.name,
        parentName: f.parentName || null
      }));
      
      await updateDoc(userRef, {
        folders: foldersPlain
      });
      
      // Local update is handled by subscription usually, but for immediate feedback:
      // userService.setUser(..., { ...userService.currentUser, folders: updatedFolders });
      // Actually the subscription will catch the local update if we updated the user service, 
      // but updateDoc only updates remote. 
      // The snapshot listener in App.js (if exists) would update userService.
      // If not, we might need to trigger a reload or manually update userService.
      // Let's check if App.js listens to Firestore. 
      // For now, assuming real-time listener exists or we need to reload.
      // To be safe, let's force a re-fetch or optimistic update if needed.
      // But wait, UserService doesn't have a "setFolders" method exposed.
      // We rely on the app's main listener.
    } catch (error) {
      console.error("Error updating folders:", error);
      toast.error("Failed to save folder changes");
    }
  };

  const handleAddFolder = async () => {
    if (!newFolderName.trim()) return;

    const newFolder = {
      id: crypto.randomUUID(),
      name: newFolderName.trim(),
      parentName: null
    };

    const updatedFolders = [...folders, newFolder];
    await handleSaveUserFolders(updatedFolders);
    setNewFolderName('');
    setIsAdding(false);
  };

  const handleDeleteFolder = async (folderId) => {
    if (window.confirm('Are you sure you want to delete this folder? Notes in it will not be deleted but might lose their folder association.')) {
      const updatedFolders = folders.filter(f => f.id !== folderId);
      await handleSaveUserFolders(updatedFolders);
    }
  };

  const startEditing = (folder) => {
    setEditingFolderId(folder.id);
    setEditName(folder.name);
  };

  const saveEditing = async () => {
    if (!editName.trim()) return;
    
    const updatedFolders = folders.map(f => 
      f.id === editingFolderId ? { ...f, name: editName.trim() } : f
    );
    
    await handleSaveUserFolders(updatedFolders);
    setEditingFolderId(null);
    setEditName('');
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60] flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
            >
                <XMarkIcon className="h-6 w-6" />
            </button>
            <h2 className="text-lg font-semibold text-gray-900">Folders</h2>
            <button
                onClick={() => setIsEditing(!isEditing)}
                className="text-orange-500 hover:text-orange-600 font-medium transition-colors"
            >
                {isEditing ? t('common.save') : t('common.edit')}
            </button>
        </div>

        {/* List */}
        <div className="overflow-y-auto flex-1 p-2">
            {folders.length === 0 && !isAdding && (
                <div className="text-center py-8 text-gray-500">
                    No folders yet. Create one!
                </div>
            )}

            <div className="space-y-1">
                {folders.map(folder => (
                    <div 
                        key={folder.id}
                        className="flex items-center px-4 py-3 bg-white hover:bg-gray-50 rounded-md group"
                    >
                        {/* Delete button (only in edit mode) */}
                        {isEditing && (
                            <button
                                onClick={() => handleDeleteFolder(folder.id)}
                                className="mr-3 text-red-500 hover:text-red-600 p-1 rounded-full hover:bg-red-50"
                            >
                                <TrashIcon className="h-5 w-5" />
                            </button>
                        )}

                        {/* Icon */}
                        {!isEditing && (
                            <FolderIcon className="h-5 w-5 mr-3 text-gray-400" />
                        )}

                        {/* Name */}
                        <div className="flex-1">
                            {editingFolderId === folder.id ? (
                                <div className="flex items-center">
                                    <input
                                        type="text"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        className="flex-1 border border-orange-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') saveEditing();
                                            if (e.key === 'Escape') setEditingFolderId(null);
                                        }}
                                    />
                                    <button 
                                        onClick={saveEditing}
                                        className="ml-2 text-green-600 hover:text-green-700"
                                    >
                                        <CheckIcon className="h-5 w-5" />
                                    </button>
                                </div>
                            ) : (
                                <span 
                                    className="text-gray-700 cursor-pointer"
                                    onClick={() => isEditing && startEditing(folder)}
                                >
                                    {folder.name}
                                </span>
                            )}
                        </div>

                        {/* Edit Indicator (only in edit mode and not currently editing) */}
                        {isEditing && editingFolderId !== folder.id && (
                            <button
                                onClick={() => startEditing(folder)}
                                className="ml-2 text-gray-400 hover:text-orange-500"
                            >
                                <PencilIcon className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                ))}

                {/* Add Folder Input */}
                {isAdding && (
                    <div className="flex items-center px-4 py-3 bg-gray-50 rounded-md border border-orange-200">
                        <FolderIcon className="h-5 w-5 mr-3 text-orange-500" />
                        <input
                            type="text"
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            placeholder="New Folder Name"
                            className="flex-1 bg-transparent border-none focus:ring-0 text-sm placeholder-gray-400"
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleAddFolder();
                                if (e.key === 'Escape') setIsAdding(false);
                            }}
                        />
                        <button 
                            onClick={handleAddFolder}
                            className="ml-2 text-orange-600 hover:text-orange-700 font-medium text-sm"
                        >
                            Add
                        </button>
                    </div>
                )}
            </div>
        </div>

        {/* Footer / Add Button */}
        {!isAdding && (
            <div className="p-4 border-t border-gray-100">
                <button
                    className="w-full flex items-center justify-center px-4 py-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                    onClick={() => setIsAdding(true)}
                >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    <span className="font-medium">Add Folder</span>
                </button>
            </div>
        )}
      </div>
    </div>
  );
}

FolderManagerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default FolderManagerModal;

